import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Calculator, Truck, Lock, History, Info } from "lucide-react";
import { EditableGarmentLine, formatCurrency } from "@/components/quote";

export const QuoteSummaryPanel = ({
  job,
  setJob,
  setHasUnsavedChanges,
  quote,
  quantityBreakResults,
  selectedQtyColumn,
  isCalculating,
  isCalculatingQtyBreak,
  isAdmin,
  showInternal,
  setShowInternal,
  showDesignFeeToClient,
  setShowDesignFeeToClient,
  settings,
  updateGarmentOverrides,
  hasApprovedSnapshot,
  latestSnapshot,
  snapshots,
  showSnapshotHistory,
  setShowSnapshotHistory,
  selectedSnapshot,
  setSelectedSnapshot,
}) => {
  return (
              <div className="bg-white rounded-lg shadow-md border border-blue-200" data-testid="quote-summary-card">
                <div className="px-4 py-3 bg-gradient-to-r from-blue-50 to-blue-100/50 rounded-t-lg border-b border-blue-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-semibold text-blue-900">Quote Summary</h3>
                      {job.is_quantity_break && quantityBreakResults?.results?.[selectedQtyColumn] && (
                        <Badge className="bg-purple-100 text-purple-700 border-purple-300 text-xs">
                          {quantityBreakResults.results[selectedQtyColumn].quantity} units
                        </Badge>
                      )}
                    </div>
                    {(isCalculating || isCalculatingQtyBreak) && (
                      <span className="text-xs text-blue-600 animate-pulse">
                        Calculating...
                      </span>
                    )}
                  </div>
                </div>
                <div className="p-4">
                  {/* Compute active quote data - use quantity break column data when in QTY BREAK mode */}
                  {(() => {
                    const activeQuote = job.is_quantity_break && quantityBreakResults?.results?.[selectedQtyColumn]
                      ? quantityBreakResults.results[selectedQtyColumn]
                      : quote;
                    const activeColumnSettings = job.is_quantity_break && job.quantity_break_settings?.[selectedQtyColumn]
                      ? job.quantity_break_settings[selectedQtyColumn]
                      : null;
                    
                    if (!activeQuote && !quote) {
                      return (
                        <div className="text-center py-6 text-gray-400">
                          <Calculator className="w-10 h-10 mx-auto mb-2 opacity-30" />
                          <p className="text-sm">Add garments to see quote</p>
                        </div>
                      );
                    }
                    
                    // Use activeQuote for QTY BREAK mode, regular quote otherwise
                    const displayQuote = job.is_quantity_break ? activeQuote : quote;
                    if (!displayQuote) {
                      return (
                        <div className="text-center py-6 text-gray-400">
                          <Calculator className="w-10 h-10 mx-auto mb-2 opacity-30" />
                          <p className="text-sm">Add garments to see quote</p>
                        </div>
                      );
                    }
                    
                    // Get garment lines - handle both regular quote and quantity break format
                    const garmentLines = displayQuote.garment_lines || [];
                    const productionTotalExGst = displayQuote.production_total_ex_gst ?? displayQuote.subtotal_ex_gst ?? 0;
                    const designFeeSell = displayQuote.design_fee_sell ?? displayQuote.design_fee ?? 0;
                    const smallOrderSurcharge = displayQuote.small_order_surcharge ?? displayQuote.small_order_fee ?? 0;
                    const smallOrderFeeReason = displayQuote.small_order_fee_reason;
                    const totalIncGst = displayQuote.total_inc_gst ?? 0;
                    const gst = displayQuote.gst ?? 0;
                    const totalGarments = displayQuote.total_garments ?? garmentLines.reduce((sum, l) => sum + (l.quantity || 0), 0);
                    const totalGarmentCost = displayQuote.total_garment_cost ?? 0;
                    const totalDecorationCost = displayQuote.total_decoration_cost ?? 0;
                    const totalSupplierShipping = displayQuote.total_supplier_shipping ?? 0;
                    const productionTrueCost = displayQuote.production_true_cost ?? 0;
                    const marginDollars = displayQuote.margin_dollars ?? 0;
                    const marginPercent = displayQuote.margin_percent ?? displayQuote.actual_margin_percent ?? 0;
                    const marginPerHour = displayQuote.margin_per_hour ?? 0;
                    const estimatedHours = displayQuote.estimated_hours ?? 0;
                    const columnShipping = activeColumnSettings?.shipping ?? job.shipping ?? 0;
                    const isRetail = job.client_type === "Retail";
                    
                    return (
                    <div className="space-y-5">
                      {/* View Toggle - Admin only */}
                      {isAdmin && (
                        <div className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                          <Label htmlFor="show-internal" className="text-sm cursor-pointer text-gray-600">
                            Show Internal View
                          </Label>
                          <Switch
                            id="show-internal"
                            checked={showInternal}
                            onCheckedChange={setShowInternal}
                            data-testid="show-internal-toggle"
                          />
                        </div>
                      )}

                      {/* ==================== CLIENT VIEW ==================== */}
                      <div className="space-y-3" data-testid="client-view">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                          Line Items {job.is_quantity_break && `(${activeColumnSettings?.quantity || 0} units)`}
                        </p>

                        {/* Garment Line Items with Inline Editable Pricing */}
                        <div className="space-y-1">
                          {garmentLines.map((line, index) => {
                            // Find the corresponding garment for this line
                            const garment = job.garments.find(g => g.id === line.id);
                            return (
                              <EditableGarmentLine
                                key={line.id}
                                line={line}
                                garment={garment}
                                globalMargin={activeColumnSettings?.margin_percent ?? job.margin_percent}
                                onUpdateOverrides={(overrides) => updateGarmentOverrides(line.id, overrides)}
                                isAdmin={isAdmin}
                              />
                            );
                          })}
                        </div>

                        {/* Design Fee Toggle (for client visibility) */}
                        {designFeeSell > 0 && (
                          <div className="flex items-center justify-between p-2 bg-muted/30 rounded text-sm">
                            <Label htmlFor="show-design-fee" className="cursor-pointer text-muted-foreground">
                              Show Design Fee to Client
                            </Label>
                            <Switch
                              id="show-design-fee"
                              checked={showDesignFeeToClient}
                              onCheckedChange={setShowDesignFeeToClient}
                              data-testid="show-design-fee-toggle"
                            />
                          </div>
                        )}

                        {/* Traditional Screen Print one-off charges (line items) */}
                        {(() => {
                          const charges = [];
                          (displayQuote.artwork_breakdown || []).forEach((b) => {
                            if (b.method === "Traditional Screen Print" && Array.isArray(b.charge_lines)) {
                              b.charge_lines.forEach((c) => charges.push({ ...c, placement: b.placement }));
                            }
                          });
                          if (charges.length === 0) return null;
                          return (
                            <div className="space-y-1 pt-2 border-t" data-testid="tsp-summary-charges">
                              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">One-Off Job Charges</p>
                              {charges.map((c, i) => (
                                <div key={i} className="flex justify-between items-center text-sm" data-testid={`tsp-summary-charge-${i}`}>
                                  <span className="text-gray-600">
                                    {c.name}{c.qty_label ? ` (${c.qty_label})` : ""}{c.placement ? ` · ${c.placement}` : ""}
                                  </span>
                                  <span className="font-mono">{formatCurrency(c.amount)}</span>
                                </div>
                              ))}
                            </div>
                          );
                        })()}

                        {/* Client Totals */}
                        {isRetail ? (
                        <div className="space-y-2 pt-2" data-testid="retail-sale-summary">
                          {(() => {
                            const printLines = (displayQuote.artwork_breakdown || [])
                              .filter((b) => b.retail_unit_price != null && b.method !== "Manual Override" && (b.placements || 0) > 0)
                              .map((b) => ({
                                placement: b.placement || "Print",
                                size_tier: b.size_tier || "",
                                amount: (b.retail_unit_price || 0) * (b.placements || 0),
                              }));
                            const garmentSubtotal = garmentLines.reduce((s, l) => s + (l.line_garment_cost || 0), 0);
                            const printSubtotal = printLines.reduce((s, p) => s + p.amount, 0);
                            const grossSubtotal = garmentSubtotal + printSubtotal;
                            const totalDiscounts = Math.max(0, grossSubtotal - totalIncGst);
                            const subtotalNet = totalIncGst - gst;
                            return (
                              <>
                                <div className="flex justify-between items-center text-base font-semibold pt-1">
                                  <span>Subtotal</span>
                                  <span className="font-mono" data-testid="retail-subtotal">{formatCurrency(grossSubtotal)}</span>
                                </div>
                                {printLines.length > 0 && (
                                  <div className="space-y-1">
                                    <p className="text-xs text-gray-500">Print Costs (inc GST)</p>
                                    {printLines.map((p, i) => (
                                      <div key={i} className="flex justify-between items-center text-sm" data-testid={`retail-print-line-${i}`}>
                                        <span className="text-gray-600">{[p.placement, p.size_tier].filter(Boolean).join(" / ")}</span>
                                        <span className="font-mono">{formatCurrency(p.amount)}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {totalDiscounts > 0.005 && (
                                  <div className="flex justify-between items-center text-sm text-green-700" data-testid="retail-total-discounts">
                                    <span>Total Discounts Given</span>
                                    <span className="font-mono">-{formatCurrency(totalDiscounts)}</span>
                                  </div>
                                )}
                                <Separator />
                                <div className="flex justify-between items-center text-sm">
                                  <span>Subtotal (inc GST)</span>
                                  <span className="font-mono" data-testid="retail-subtotal-inc-gst">{formatCurrency(subtotalNet)}</span>
                                </div>
                                <div className="flex justify-between items-center text-sm">
                                  <span>Includes GST (10%)</span>
                                  <span className="font-mono" data-testid="retail-gst-included">{formatCurrency(gst)}</span>
                                </div>
                                <Separator />
                                <div className="flex justify-between items-center text-lg font-bold">
                                  <span>Total inc GST</span>
                                  <span className="font-mono text-orange-600" data-testid="retail-total-inc-gst">{formatCurrency(totalIncGst)}</span>
                                </div>
                              </>
                            );
                          })()}
                        </div>
                        ) : (
                        <div className="space-y-2 pt-2">
                          <div className="flex justify-between items-center text-sm">
                            <span>Subtotal</span>
                            <span className="font-mono">
                              {formatCurrency(productionTotalExGst)}
                            </span>
                          </div>
                          
                          {designFeeSell > 0 && showDesignFeeToClient && (
                            <div className="flex justify-between items-center text-sm">
                              <span>Design Fee</span>
                              <span className="font-mono" data-testid="design-fee-client">
                                {formatCurrency(designFeeSell)}
                              </span>
                            </div>
                          )}

                          {/* Small Order Fee with smart exceptions */}
                          {smallOrderFeeReason && smallOrderFeeReason !== "not_applicable" && (
                            <div className="space-y-1">
                              {smallOrderSurcharge > 0 ? (
                                <div className="flex justify-between items-center text-sm text-amber-600">
                                  <div className="flex items-center gap-2">
                                    <span>Small Order Fee</span>
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Info className="w-3.5 h-3.5 text-amber-500 cursor-help" />
                                        </TooltipTrigger>
                                        <TooltipContent className="max-w-xs">
                                          <p className="text-xs">Applied because order is under {settings?.small_order_threshold || 10} garments</p>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  </div>
                                  <span className="font-mono" data-testid="small-order-surcharge">
                                    {formatCurrency(smallOrderSurcharge)}
                                  </span>
                                </div>
                              ) : (
                                <div className="flex justify-between items-center text-sm text-green-600">
                                  <div className="flex items-center gap-2">
                                    <span>Small Order Fee</span>
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Info className="w-3.5 h-3.5 text-green-500 cursor-help" />
                                        </TooltipTrigger>
                                        <TooltipContent className="max-w-xs">
                                          <p className="text-xs">
                                            {smallOrderFeeReason === "waived_high_value" 
                                              ? `Waived: Order value exceeds $${settings?.small_order_value_threshold || 180}`
                                              : "Waived: Manual override applied"
                                            }
                                          </p>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  </div>
                                  <span className="font-mono line-through text-muted-foreground" data-testid="small-order-surcharge-waived">
                                    {formatCurrency(settings?.small_order_surcharge || 0)}
                                    <span className="ml-2 no-underline text-green-600">Waived</span>
                                  </span>
                                </div>
                              )}
                              {/* Manual override toggle - only show when fee could apply */}
                              {totalGarments < (settings?.small_order_threshold || 10) && (
                                <div className="flex items-center justify-between text-xs pt-1">
                                  <div className="flex items-center gap-2">
                                    <Switch
                                      id="apply-small-order-fee"
                                      checked={job.apply_small_order_fee !== false}
                                      onCheckedChange={(checked) => {
                                        setJob(prev => ({ ...prev, apply_small_order_fee: checked }));
                                        setHasUnsavedChanges(true);
                                      }}
                                      data-testid="apply-small-order-fee-toggle"
                                      className="scale-75"
                                    />
                                    <Label htmlFor="apply-small-order-fee" className="text-xs text-muted-foreground cursor-pointer">
                                      Apply Small Order Fee
                                    </Label>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Client Shipping - use column shipping in QTY BREAK mode */}
                          {columnShipping > 0 && (
                            <div className="flex justify-between items-center text-sm text-blue-600">
                              <span>Shipping & Handling</span>
                              <span className="font-mono" data-testid="shipping-fee">
                                {formatCurrency(columnShipping)}
                              </span>
                            </div>
                          )}

                          <Separator />
                          
                          <div className="flex justify-between items-center text-sm">
                            <span>Subtotal ex GST</span>
                            <span className="font-mono" data-testid="subtotal-ex-gst">
                              {formatCurrency(
                                productionTotalExGst + 
                                (showDesignFeeToClient ? designFeeSell : 0) +
                                (smallOrderSurcharge || 0) +
                                (columnShipping || 0)
                              )}
                            </span>
                          </div>
                          
                          <div className="flex justify-between items-center text-sm">
                            <span>GST (10%)</span>
                            <span className="font-mono" data-testid="gst-amount">
                              {formatCurrency(
                                showDesignFeeToClient 
                                  ? gst 
                                  : productionTotalExGst * 0.1
                              )}
                            </span>
                          </div>
                          
                          <Separator />
                          
                          <div className="flex justify-between items-center text-lg font-bold">
                            <span>Total inc GST</span>
                            <span
                              className="font-mono text-accent"
                              data-testid="total-inc-gst"
                            >
                              {formatCurrency(
                                showDesignFeeToClient 
                                  ? totalIncGst 
                                  : productionTotalExGst * 1.1
                              )}
                            </span>
                          </div>
                        </div>
                        )}
                      </div>

                      {/* ==================== INTERNAL VIEW ==================== */}
                      {isAdmin && showInternal && (
                        <div 
                          className="space-y-4 p-4 rounded-lg bg-slate-100 border-2 border-slate-300" 
                          data-testid="internal-view"
                        >
                          <div className="flex items-center gap-2">
                            <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">
                              Internal View
                            </h4>
                            <Badge className="text-xs bg-slate-600">Admin Only</Badge>
                          </div>

                          {/* Cost Breakdown */}
                          <div className="grid grid-cols-2 gap-3">
                            <div className="bg-white p-3 rounded border">
                              <p className="text-xs text-slate-500 mb-1">Total Garment Cost</p>
                              <p className="font-mono font-medium text-slate-800" data-testid="internal-garment-cost">
                                {formatCurrency(totalGarmentCost)}
                              </p>
                            </div>
                            <div className="bg-white p-3 rounded border">
                              <p className="text-xs text-slate-500 mb-1">Total Decoration Cost</p>
                              <p className="font-mono font-medium text-slate-800" data-testid="internal-decoration-cost">
                                {formatCurrency(totalDecorationCost)}
                              </p>
                            </div>
                            {totalSupplierShipping > 0 && (
                              <div className="bg-amber-50 p-3 rounded border border-amber-200">
                                <p className="text-xs text-amber-700 mb-1 flex items-center gap-1">
                                  <Truck className="w-3 h-3" />
                                  Total Supplier Shipping
                                </p>
                                <p className="font-mono font-medium text-amber-800" data-testid="internal-supplier-shipping">
                                  {formatCurrency(totalSupplierShipping)}
                                </p>
                              </div>
                            )}
                            {job.misc_costs > 0 && (
                              <div className="bg-white p-3 rounded border">
                                <p className="text-xs text-slate-500 mb-1">Misc Costs</p>
                                <p className="font-mono font-medium text-slate-800" data-testid="internal-misc-costs">
                                  {formatCurrency(job.misc_costs)}
                                </p>
                              </div>
                            )}
                          </div>

                          {/* Per-Line Margin Details (when overrides exist) */}
                          {garmentLines?.some(l => l.margin_override !== null || l.price_adjustment !== null) && (
                            <>
                              <Separator className="bg-slate-300" />
                              <div className="space-y-2">
                                <p className="text-xs font-medium text-slate-600 uppercase tracking-wider">
                                  Per-Garment Margin Details
                                </p>
                                {garmentLines?.map((line, idx) => (
                                  <div 
                                    key={line.id}
                                    className={`flex justify-between items-center text-xs p-2 rounded ${
                                      line.margin_override !== null || line.price_adjustment !== null
                                        ? 'bg-blue-50 border border-blue-200'
                                        : 'bg-white border'
                                    }`}
                                  >
                                    <span className="truncate flex-1 mr-2">{line.display_name || line.name}</span>
                                    <div className="flex items-center gap-2 shrink-0">
                                      {line.margin_override !== null && (
                                        <Badge variant="outline" className="text-[10px] bg-blue-100 text-blue-700 border-blue-300">
                                          {(line.margin_override * 100).toFixed(0)}% margin
                                        </Badge>
                                      )}
                                      {line.price_adjustment !== null && line.price_adjustment !== 0 && (
                                        <Badge variant="outline" className="text-[10px] bg-emerald-100 text-emerald-700 border-emerald-300">
                                          {line.price_adjustment >= 0 ? '+' : ''}${line.price_adjustment.toFixed(2)}/ea
                                        </Badge>
                                      )}
                                      {line.effective_margin !== null && line.effective_margin !== undefined && (
                                        <span className="font-mono text-slate-600">
                                          {(line.effective_margin * 100).toFixed(1)}%
                                        </span>
                                      )}
                                      <span className="font-mono font-medium">
                                        {formatCurrency(line.line_total)}
                                      </span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </>
                          )}

                          <Separator className="bg-slate-300" />

                          {/* Production True Cost */}
                          <div className="bg-slate-200 p-3 rounded">
                            <div className="flex justify-between items-center">
                              <span className="text-sm font-medium text-slate-700">Production True Cost</span>
                              <span className="font-mono font-bold text-slate-800" data-testid="internal-true-cost">
                                {formatCurrency(productionTrueCost)}
                              </span>
                            </div>
                          </div>

                          {/* True Profit (Margin) - Highlighted */}
                          <div className="bg-green-100 p-3 rounded border-2 border-green-400">
                            <div className="flex justify-between items-center">
                              <span className="text-sm font-semibold text-green-800">True Profit</span>
                              <span className="font-mono font-bold text-lg text-green-800" data-testid="internal-true-profit">
                                {formatCurrency(marginDollars)}
                              </span>
                            </div>
                            <p className="text-xs text-green-700 mt-1">
                              Sell ({formatCurrency(productionTotalExGst)}) − True Cost ({formatCurrency(productionTrueCost)})
                            </p>
                          </div>

                          {/* Margin Metrics */}
                          <div className="grid grid-cols-2 gap-3">
                            <div className="bg-green-50 p-3 rounded border border-green-200">
                              <p className="text-xs text-green-700 mb-1">Margin %</p>
                              <p className="font-mono font-bold text-green-800" data-testid="internal-margin-percent">
                                {marginPercent}%
                              </p>
                            </div>
                            <div className="bg-blue-50 p-3 rounded border border-blue-200">
                              <p className="text-xs text-blue-700 mb-1">Margin per Hour</p>
                              <p className="font-mono font-bold text-blue-800" data-testid="internal-margin-per-hour">
                                {formatCurrency(marginPerHour)}/hr
                              </p>
                            </div>
                          </div>

                          {/* Production Hours */}
                          <div className="bg-blue-50 p-3 rounded border border-blue-200">
                            <div className="flex justify-between items-center">
                              <span className="text-xs text-blue-700">Est. Production Hours</span>
                              <span className="font-mono font-medium text-blue-800" data-testid="internal-est-hours">
                                {estimatedHours}h
                              </span>
                            </div>
                          </div>

                          {/* Method Comparison (Internal) - only in normal mode */}
                          {!job.is_quantity_break && quote?.method_comparison && (
                            <>
                              <Separator className="bg-slate-300" />
                              <div className="space-y-2">
                                <p className="text-xs font-medium text-slate-600 uppercase tracking-wider">
                                  Method Comparison
                                </p>
                                <div className="grid grid-cols-2 gap-2">
                                  <div
                                    className={`p-2 rounded border ${
                                      quote.method_comparison.recommended === "Screen Print Pro"
                                        ? "bg-green-100 border-green-400"
                                        : "bg-white"
                                    }`}
                                  >
                                    <p className="text-xs text-slate-500">Screen Print</p>
                                    <p className="font-mono text-sm font-medium">
                                      {formatCurrency(quote.method_comparison.screen_print_pro)}
                                    </p>
                                  </div>
                                  <div
                                    className={`p-2 rounded border ${
                                      quote.method_comparison.recommended === "UltraColour Pro"
                                        ? "bg-green-100 border-green-400"
                                        : "bg-white"
                                    }`}
                                  >
                                    <p className="text-xs text-slate-500">UC Pro</p>
                                    <p className="font-mono text-sm font-medium">
                                      {formatCurrency(quote.method_comparison.ultracolour_pro)}
                                    </p>
                                  </div>
                                </div>
                                <p className="text-xs text-center text-slate-600">
                                  Save{" "}
                                  <span className="font-mono font-medium text-green-700">
                                    {formatCurrency(quote.method_comparison.savings)}
                                  </span>{" "}
                                  with {quote.method_comparison.recommended}
                                </p>
                              </div>
                            </>
                          )}
                        </div>
                      )}

                      {/* ==================== APPROVED SNAPSHOT ==================== */}
                      {hasApprovedSnapshot && (
                        <div 
                          className="space-y-4 p-4 rounded-lg bg-green-50 border-2 border-green-300" 
                          data-testid="approved-snapshot-section"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Lock className="w-4 h-4 text-green-700" />
                              <h4 className="text-sm font-semibold text-green-800 uppercase tracking-wider">
                                Approved Snapshot
                              </h4>
                              <Badge className="text-xs bg-green-600">v{latestSnapshot?.version}</Badge>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-green-700 hover:text-green-800"
                              onClick={() => setShowSnapshotHistory(!showSnapshotHistory)}
                              data-testid="toggle-snapshot-history"
                            >
                              <History className="w-4 h-4 mr-1" />
                              {snapshots.length > 1 ? `${snapshots.length} versions` : "History"}
                            </Button>
                          </div>
                          
                          <p className="text-xs text-green-700">
                            Approved on {latestSnapshot?.timestamp ? new Date(latestSnapshot.timestamp).toLocaleString("en-AU") : "—"}
                          </p>

                          {/* Snapshot Client View */}
                          <div className="space-y-2 bg-white p-3 rounded border border-green-200">
                            <p className="text-xs font-medium text-green-800 uppercase tracking-wider">Client Totals (Frozen)</p>
                            {latestSnapshot?.garment_lines?.map((line, idx) => (
                              <div key={idx} className="py-1 border-b last:border-b-0 border-green-100">
                                <div className="flex justify-between text-sm">
                                  <span className="text-slate-600">{line.display_name || line.name} × {line.quantity}</span>
                                  <span className="font-mono">{formatCurrency(line.line_total)}</span>
                                </div>
                                {line.decoration_summary && (
                                  <p className="text-xs text-blue-600 mt-0.5">
                                    Decoration: {line.decoration_summary}
                                  </p>
                                )}
                              </div>
                            ))}
                            <Separator className="my-2" />
                            <div className="flex justify-between text-sm">
                              <span>Subtotal ex GST</span>
                              <span className="font-mono">{formatCurrency(latestSnapshot?.subtotal_ex_gst)}</span>
                            </div>
                            {latestSnapshot?.design_shown_to_client && latestSnapshot?.design_fee_sell > 0 && (
                              <div className="flex justify-between text-sm">
                                <span>Design Fee</span>
                                <span className="font-mono">{formatCurrency(latestSnapshot?.design_fee_sell)}</span>
                              </div>
                            )}
                            <div className="flex justify-between text-sm">
                              <span>GST</span>
                              <span className="font-mono">{formatCurrency(latestSnapshot?.gst)}</span>
                            </div>
                            <div className="flex justify-between text-sm font-bold">
                              <span>Total inc GST</span>
                              <span className="font-mono text-green-700">{formatCurrency(latestSnapshot?.total_inc_gst)}</span>
                            </div>
                          </div>

                          {/* Snapshot Internal View - Only show if internal view is enabled */}
                          {showInternal && (
                            <div className="space-y-2 bg-slate-100 p-3 rounded border border-slate-300">
                              <p className="text-xs font-medium text-slate-700 uppercase tracking-wider">Internal Snapshot (Frozen)</p>
                              <div className="grid grid-cols-2 gap-2 text-xs">
                                <div>
                                  <span className="text-slate-500">Garment Cost:</span>
                                  <span className="font-mono ml-1">{formatCurrency(latestSnapshot?.total_garment_cost)}</span>
                                </div>
                                <div>
                                  <span className="text-slate-500">Decoration:</span>
                                  <span className="font-mono ml-1">{formatCurrency(latestSnapshot?.total_decoration_cost)}</span>
                                </div>
                                <div>
                                  <span className="text-slate-500">Supplier Ship:</span>
                                  <span className="font-mono ml-1">{formatCurrency(latestSnapshot?.total_supplier_shipping)}</span>
                                </div>
                                <div>
                                  <span className="text-slate-500">Misc:</span>
                                  <span className="font-mono ml-1">{formatCurrency(latestSnapshot?.misc_costs)}</span>
                                </div>
                              </div>
                              <Separator className="my-2" />
                              <div className="flex justify-between text-sm">
                                <span className="text-slate-600">Production True Cost</span>
                                <span className="font-mono">{formatCurrency(latestSnapshot?.production_true_cost)}</span>
                              </div>
                              <div className="grid grid-cols-2 gap-2 text-xs">
                                <div className="bg-green-100 p-2 rounded">
                                  <span className="text-green-700">Margin %:</span>
                                  <span className="font-mono font-bold ml-1">{latestSnapshot?.margin_percent}%</span>
                                </div>
                                <div className="bg-green-100 p-2 rounded">
                                  <span className="text-green-700">Margin $:</span>
                                  <span className="font-mono font-bold ml-1">{formatCurrency(latestSnapshot?.margin_dollars)}</span>
                                </div>
                                <div className="bg-blue-100 p-2 rounded">
                                  <span className="text-blue-700">Est. Hours:</span>
                                  <span className="font-mono ml-1">{latestSnapshot?.estimated_hours}h</span>
                                </div>
                                <div className="bg-blue-100 p-2 rounded">
                                  <span className="text-blue-700">$/Hour:</span>
                                  <span className="font-mono font-bold ml-1">{formatCurrency(latestSnapshot?.margin_per_hour)}</span>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Snapshot History */}
                          {showSnapshotHistory && snapshots.length > 0 && (
                            <div className="space-y-2 bg-white p-3 rounded border border-green-200">
                              <p className="text-xs font-medium text-green-800 uppercase tracking-wider">Revision History</p>
                              <div className="space-y-1 max-h-40 overflow-y-auto">
                                {[...snapshots].reverse().map((snap, idx) => (
                                  <div 
                                    key={snap.version}
                                    className={`flex items-center justify-between p-2 rounded text-xs cursor-pointer transition-colors ${
                                      selectedSnapshot?.version === snap.version 
                                        ? "bg-green-100 border border-green-300" 
                                        : "hover:bg-slate-50"
                                    }`}
                                    onClick={() => setSelectedSnapshot(selectedSnapshot?.version === snap.version ? null : snap)}
                                  >
                                    <div className="flex items-center gap-2">
                                      <Badge variant="outline" className="text-xs">v{snap.version}</Badge>
                                      <span className="text-slate-600">
                                        {new Date(snap.timestamp).toLocaleString("en-AU", { 
                                          day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" 
                                        })}
                                      </span>
                                    </div>
                                    <span className="font-mono text-green-700">{formatCurrency(snap.total_inc_gst)}</span>
                                  </div>
                                ))}
                              </div>
                              
                              {/* Selected snapshot detail */}
                              {selectedSnapshot && selectedSnapshot.version !== latestSnapshot?.version && (
                                <div className="mt-2 p-2 bg-slate-50 rounded border text-xs">
                                  <p className="font-medium mb-1">Snapshot v{selectedSnapshot.version} Details:</p>
                                  <div className="grid grid-cols-2 gap-1 text-slate-600">
                                    <span>Subtotal: {formatCurrency(selectedSnapshot.subtotal_ex_gst)}</span>
                                    <span>GST: {formatCurrency(selectedSnapshot.gst)}</span>
                                    <span>Total: {formatCurrency(selectedSnapshot.total_inc_gst)}</span>
                                    <span>Margin: {selectedSnapshot.margin_percent}%</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    );
                  })()}
                </div>
              </div>
  );
};
